import { combineReducers } from 'redux'
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux'
import { routerMiddleware, connectRouter } from 'connected-react-router'
import createSagaMiddleware from 'redux-saga'
import { all, fork } from 'redux-saga/effects'
import { adminReducer, adminSaga, USER_LOGOUT } from 'react-admin'
import { configureStore } from '@reduxjs/toolkit'
import { History } from 'history'

import microservicesReducer from './microservices/reducer'
import definitionsReducer from './definitions/definitions.reducer'
import apiReducer from './api/api.reducer'
import editorReducer from './editor/editor.reducer'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createRootReducer = (history: History) => {
  return combineReducers({
    admin: adminReducer,
    router: connectRouter(history),
    microservices: microservicesReducer,
    definitions: definitionsReducer,
    api: apiReducer,
    editor: editorReducer,
  })
}

export type TAppState = ReturnType<ReturnType<typeof createRootReducer>>
export type TAppDispatch = ReturnType<typeof createAdminStore>['dispatch']

const createAdminStore = ({
  authProvider,
  dataProvider,
  history,
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
}) => {
  const rootReducer = createRootReducer(history)
  const sagaMiddleware = createSagaMiddleware()

  const store = configureStore({
    reducer: (state, action) => {
      return rootReducer(
        action.type !== USER_LOGOUT ? state : undefined,
        action,
      )
    },
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        thunk: false,
        serializableCheck: false,
        immutableCheck: false,
      }).concat(sagaMiddleware, routerMiddleware(history)),
    devTools: process.env.NODE_ENV !== 'production',
  })

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sagaMiddleware.run(function* () {
    yield all([adminSaga(dataProvider, authProvider)].map(fork))
  })

  return store
}

export const useAppSelector: TypedUseSelectorHook<TAppState> = useReduxSelector

export default createAdminStore
